import type { Lot, LotEvent } from '../types/lot'
import type { FeedLog, HarvestLog, LotCostEvent } from '../types/production'
import { normalizeSpeciesKey, resolveExpectedFcrRange } from '../data/productionSpeciesPresets'
import { computeFcrInFlight, computeFcrInFlightBand, type FeedConversionRatioBand } from './feedConversionRatio'
import { computeBiologicalFcr, computeEconomicFcr, computeNetProductionKg, type LotFcrInputs } from './lotFeedConversion'

/**
 * v2.2 T3-D-05: cost-per-kg dataset derivation.
 *
 * Produces one row per lot that has at least one `HarvestLog` or one
 * `LotCostEvent`, aggregating harvest weight (SUM `harvestKg` across
 * events, mirrors the T3-D-02 rollup semantics) and cost totals (SUM
 * `amountCents` from paired `LotCostEvent` entries) into a single
 * denormalized row consumable by the Reports v1 dataset registry.
 *
 * Cost-per-kg is computed in the same currency as the underlying cost
 * events — if a lot has events in more than one currency, `currency` is
 * flagged as `'MIXED'` and `costPerKgMajor` is `null` (cannot sum across
 * currencies without an exchange rate).
 *
 * Category breakdown collapses the 9-value `categoryPrefix` enum
 * (G/N/H/L/I/M/A/F/R) into 5 display buckets: G (genetics), N (nutrition
 * / feed, auto-derived), H (health), L (labor), and `other` (everything
 * else — I/M/A/F/R are indirect/fixed overhead categories). This matches
 * the T3-B-03c/d manual-entry UI which only exposes G/H/L; N is
 * auto-derived from FeedLog; the rest are reserved for a future overhead
 * capture path.
 *
 * Pure and side-effect free. All arithmetic is deterministic. Does not
 * mutate any input.
 */

export type LotCostPerKgRow = {
  lotId: string
  lotKey: string
  farmId: string
  productionUnitId: string
  /** Species key from lot roster (normalized to known preset keys). */
  speciesKey: string
  /** Species expected in-flight FCR lower bound (inclusive). */
  expectedFcrMin: number | null
  /** Species expected in-flight FCR upper bound (inclusive). */
  expectedFcrMax: number | null
  /** Display label for the expected species FCR range. */
  expectedFcrRangeLabel: string | null
  /** SUM of `HarvestLog.harvestKg` across every harvest event on this lot. Decimal string, 3 decimal places. */
  totalHarvestKg: string
  /** SUM of `HarvestLog.harvestCount` across every harvest event on this lot. Integer string. */
  totalHarvestCount: string
  /** Number of contributing `HarvestLog` events. */
  harvestEventCount: number
  /** v2.2 T3-B-05a: SUM of `FeedLog.feedKg` across every feed event on this lot. Decimal string, 3 decimal places. Mirrors the `totalHarvestKg` format. Zero-format `'0.000'` when the lot has no FeedLog entries. */
  totalFeedKg: string
  /** v2.2 T3-B-05a: Number of contributing `FeedLog` events. */
  feedEventCount: number
  /** v2.2 T3-B-05a: `totalFeedKg / totalHarvestKg` with 2 decimals (biological FCR). `null` when either input is zero (cannot divide / cannot diagnose). */
  feedConversionRatio: string | null
  /** v2.2 T3-B-06e: mid-cycle in-flight FCR = `totalFeedKg / currentBiomassKg`. 2 decimals. Suppressed to `null` when the lot has any settled harvest (the settled `feedConversionRatio` is the truth after harvest), when feed is zero, or when current biomass can't be derived (missing weight or stock signal). Callers pass current biomass via the `currentBiomassByLotId` arg on `buildLotCostPerKgRows`. */
  feedConversionRatioInFlight: string | null
  /** Banding-compatible status for in-flight FCR against species expected range. */
  feedConversionRatioInFlightBand: FeedConversionRatioBand
  /** v2.2 findings.md #215: biomass at the start (window start, else stocked: first count × first mean weight), kg, 3 decimals. */
  startBiomassKg: string | null
  /** v2.2 findings.md #215: harvested kg + biomass at the end − biomass at the start, kg, 3 decimals. */
  netProductionKg: string | null
  /** v2.2 findings.md #215: deaths in the window × mean weight on their day, kg, 3 decimals. */
  deadBiomassKg: string | null
  /** v2.2 findings.md #215: eFCR = feed ÷ net production, 2 decimals. */
  economicFcr: string | null
  /** v2.2 findings.md #215: bFCR = feed ÷ (net production + dead biomass), 2 decimals. */
  biologicalFcr: string | null
  /** SUM of `LotCostEvent.amountCents` across every cost event on this lot. Integer. */
  totalCostsCents: number
  /** `totalCostsCents / 100` formatted to 2 decimal places (currency major unit). */
  totalCostsMajor: string
  /** Currency code from the contributing events, or `''` when there are no costs, or `'MIXED'` when events use more than one currency. */
  currency: string
  /** Number of contributing `LotCostEvent` records. */
  costEventCount: number
  /** `totalCostsMajor / totalHarvestKg`, 4 decimals, or `null` when harvest is 0, missing currency, or currency is mixed. */
  costPerKgMajor: string | null
  /** v2.2 T3-B-05c: MIXED-currency triage string. Empty when the lot uses a single currency (or has no cost events); otherwise a formatted, deterministic breakdown sorted by currency code ascending, e.g. `'COP:8, USD:2'`. Derived from `currencyEventCounts` — not stored on `LotCostEvent`. */
  currencyBreakdown: string
  /** Category-bucketed cost totals in major units (2 decimals). Buckets: G, N, H, L, other. */
  costByCategoryMajor: {
    G: string
    N: string
    H: string
    L: string
    other: string
  }
  /** Category-bucketed shares as percentage strings with 1 decimal (e.g. `'42.5'`). Sums to `100.0` when totalCosts > 0. All-zero row emits `'0.0'` per bucket. */
  categorySharePct: {
    G: string
    N: string
    H: string
    L: string
    other: string
  }
}

type LotAccumulator = {
  lotId: string
  lotKey: string
  farmId: string
  productionUnitId: string
  speciesKey: string
  totalHarvestKgNum: number
  totalHarvestCount: number
  harvestEventCount: number
  /** v2.2 T3-B-05a: numeric feed total (kg) accumulated across all FeedLog entries for this lot. Formatted to 3 decimals at row emit time. */
  totalFeedKgNum: number
  /** v2.2 T3-B-05a: count of contributing FeedLog entries. */
  feedEventCount: number
  totalCostsCents: number
  currencies: Set<string>
  /** v2.2 T3-B-05c: per-currency event count, populated inline with the `currencies.add` calls. Powers the MIXED-currency triage column. */
  currencyEventCounts: Map<string, number>
  costEventCount: number
  categoryCents: {
    G: number
    N: number
    H: number
    L: number
    other: number
  }
}

const CATEGORY_BUCKETS = new Set(['G', 'N', 'H', 'L'])

const parseHarvestKg = (raw: unknown): number => {
  const num = typeof raw === 'number' ? raw : Number(String(raw ?? '').replaceAll(',', '.').trim())
  return Number.isFinite(num) ? num : 0
}

/**
 * v2.2 T3-B-05a: parse `FeedLog.feedKg` values into a finite number.
 * Mirrors `parseHarvestKg` so Spanish-locale comma decimals from imports
 * survive the roll-up. Non-numeric / missing values collapse to 0 (they
 * contribute nothing to the total).
 */
const parseFeedKg = (raw: unknown): number => {
  const num = typeof raw === 'number' ? raw : Number(String(raw ?? '').replaceAll(',', '.').trim())
  return Number.isFinite(num) ? num : 0
}

const parseHarvestCount = (raw: unknown): number => {
  if (raw === undefined || raw === null || raw === '') return 0
  const num = typeof raw === 'number' ? raw : Number(String(raw).replaceAll(',', '').trim())
  return Number.isFinite(num) ? num : 0
}

const bucketFor = (prefix: string): keyof LotAccumulator['categoryCents'] => (
  CATEGORY_BUCKETS.has(prefix) ? (prefix as 'G' | 'N' | 'H' | 'L') : 'other'
)

const newAccumulator = (lot: Pick<Lot, 'id' | 'lotKey' | 'farmId' | 'productionUnitId' | 'species'>): LotAccumulator => ({
  lotId: lot.id,
  lotKey: typeof lot.lotKey === 'string' ? lot.lotKey : '',
  farmId: typeof lot.farmId === 'string' ? lot.farmId : '',
  productionUnitId: typeof lot.productionUnitId === 'string' ? lot.productionUnitId : '',
  speciesKey: normalizeSpeciesKey(lot.species),
  totalHarvestKgNum: 0,
  totalHarvestCount: 0,
  harvestEventCount: 0,
  totalFeedKgNum: 0,
  feedEventCount: 0,
  totalCostsCents: 0,
  currencies: new Set<string>(),
  currencyEventCounts: new Map<string, number>(),
  costEventCount: 0,
  categoryCents: { G: 0, N: 0, H: 0, L: 0, other: 0 }
})

const ensureAccumulator = (
  acc: Map<string, LotAccumulator>,
  lotsById: Map<string, Pick<Lot, 'id' | 'lotKey' | 'farmId' | 'productionUnitId' | 'species'>>,
  lotId: string,
  fallback: { farmId?: string; productionUnitId?: string }
): LotAccumulator | null => {
  const trimmed = typeof lotId === 'string' ? lotId.trim() : ''
  if (!trimmed) return null

  const existing = acc.get(trimmed)
  if (existing) return existing

  const lot = lotsById.get(trimmed) ?? {
    id: trimmed,
    lotKey: '',
    farmId: fallback.farmId ?? '',
    productionUnitId: fallback.productionUnitId ?? '',
    species: undefined
  }

  const created = newAccumulator(lot)
  acc.set(trimmed, created)
  return created
}

const formatPercent = (numerator: number, denominator: number): string => {
  if (denominator <= 0) return '0.0'
  return ((numerator / denominator) * 100).toFixed(1)
}

/**
 * v2.2 T3-B-06a: build the `(farmId, lotKey) -> lotId` lookup map used
 * by `resolveLotIdWithFallback`. Skips lots with no `lotKey` (they can
 * only be matched by id) or no `farmId` (the fallback key would be
 * ambiguous across farms). Later duplicates for the same
 * `(farmId, lotKey)` pair are ignored so the first-seen lot wins; the
 * `lots` roster passed to `buildLotCostPerKgRows` is expected to be
 * de-duplicated already by the caller (Dexie unique index / Prisma
 * unique constraint on `(tenantKey, farmId, lotKey)` upstream).
 *
 * A1.1 fix (docs/architecture-concerns.md §A1.1, Hallazgo D4): this map
 * only ever indexed a lot under its CURRENT `farmId`. Legacy rows that
 * predate `lotId` tagging record the EVENT's farmId at write time — for
 * a lot that has since been transferred/moved, the event's farmId is
 * the lot's OLD farmId, which no longer matches this map and the row
 * was silently dropped. When `lotEvents` is supplied, every farmId a lot
 * has occupied is indexed to that lotId, so a legacy row recorded under
 * any past location still resolves. Omitting `lotEvents` preserves the
 * prior current-farmId-only behavior exactly.
 *
 * B2 fix (tier-a-move-transfer-location-addendum.md §5): relocations are
 * not only `move`/`transfer`. `LotService.buildUpdateLedgerEvent` records
 * `fromFarmId`/`toFarmId` on a plain `update` too, so a lot relocated via
 * the generic update path was invisible here.
 *
 * B4 fix (same doc): the index used to be `farmId::lotKey → lotId` with
 * silent first-wins on collision, so when two lots have shared a
 * `(farmId, lotKey)` pair at different times every legacy row landed on
 * whichever lot happened to be seen first. It now stores the occupancy
 * INTERVALS behind each pair so a row's own `recordedAt` picks the lot
 * that actually held that key at that moment, and genuinely undecidable
 * rows are skipped rather than misattributed.
 */
type LotFarmTenure = { lotId: string; fromMs: number; toMs: number }

const parseInstantMs = (value: unknown): number => {
  if (typeof value !== 'string') return Number.NaN
  return Date.parse(value)
}

const readFarmIdMetadata = (metadata: Record<string, unknown> | undefined, key: string): string => {
  const raw = metadata?.[key]
  return typeof raw === 'string' ? raw.trim() : ''
}

type FallbackLotEvent = Pick<LotEvent, 'targetLotIds' | 'sourceLotIds' | 'eventType' | 'metadata' | 'recordedAt'>

/**
 * v2.2 Fase 3 (tier-a-merge-consumer-mapping.md §2.6): one row of the
 * materialised `LotLineageProjection`, as returned by `GET /lots/lineage`.
 * Only the fields this fallback index needs are picked, so callers can pass
 * the full `LotLineageProjectionRow` from `useLotLineageProjection` directly.
 */
type FallbackLineageRow = {
  lotId: string
  memberLotId: string
  memberLotKey: string
  farmId: string
  relation: 'self' | 'ancestor' | 'descendant'
  effectiveFrom?: string
  effectiveTo?: string
  /**
   * Fase 7 (tier-a-merge-consumer-mapping.md §2.6 line 361, consumer #13):
   * fractional biomass share this projection row's ancestor contributes to
   * `lotId`, already composed by `LotIdentityResolver.resolveMemberWeight`
   * (Fase 6 write path). `number` in [0, 1], defaults to `1` when omitted so
   * pre-Fase-6/7 callers (and any historical `weight = 1.0` row) are an
   * exact no-op — see `canonicalize` below.
   */
  weight?: number
}

/** Open bound in the given direction when the projection leaves a window unbounded. */
const projectionEpoch = (value: string | undefined, openBound: number): number => {
  if (!value) return openBound
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? openBound : parsed
}

const buildLotsByFarmAndKey = (
  lots: readonly Pick<Lot, 'id' | 'lotKey' | 'farmId'>[],
  lotEvents?: readonly FallbackLotEvent[],
  lineageRows?: readonly FallbackLineageRow[]
): Map<string, LotFarmTenure[]> => {
  const indexed: { id: string; farmId: string; lotKey: string }[] = []
  for (const lot of lots) {
    const id = typeof lot.id === 'string' ? lot.id.trim() : ''
    const farmId = typeof lot.farmId === 'string' ? lot.farmId.trim() : ''
    const lotKey = typeof lot.lotKey === 'string' ? lot.lotKey.trim() : ''
    if (!id || !farmId || !lotKey) continue
    indexed.push({ id, farmId, lotKey })
  }

  // Farm changes per lot, chronologically. `move` stays within a farm and a
  // plain `update` usually changes nothing about placement, so only events
  // that actually cross farms open a new tenure.
  const relocationsByLotId = new Map<string, { atMs: number; fromFarmId: string; toFarmId: string }[]>()
  for (const event of lotEvents ?? []) {
    if (event.eventType !== 'move' && event.eventType !== 'transfer' && event.eventType !== 'update') continue
    const metadata = event.metadata ?? {}
    const fromFarmId = readFarmIdMetadata(metadata, 'fromFarmId')
    const toFarmId = readFarmIdMetadata(metadata, 'toFarmId')
    if (!fromFarmId || !toFarmId || fromFarmId === toFarmId) continue
    const atMs = parseInstantMs(event.recordedAt)
    if (!Number.isFinite(atMs)) continue

    for (const lotId of new Set([...(event.sourceLotIds ?? []), ...(event.targetLotIds ?? [])])) {
      const trimmed = typeof lotId === 'string' ? lotId.trim() : ''
      if (!trimmed) continue
      const bucket = relocationsByLotId.get(trimmed) ?? []
      bucket.push({ atMs, fromFarmId, toFarmId })
      relocationsByLotId.set(trimmed, bucket)
    }
  }

  const map = new Map<string, LotFarmTenure[]>()
  const addTenure = (farmId: string, lotKey: string, tenure: LotFarmTenure): void => {
    const composite = `${farmId}::${lotKey}`
    const bucket = map.get(composite) ?? []
    bucket.push(tenure)
    map.set(composite, bucket)
  }

  for (const lot of indexed) {
    const relocations = (relocationsByLotId.get(lot.id) ?? []).sort((left, right) => left.atMs - right.atMs)
    if (relocations.length === 0) {
      addTenure(lot.farmId, lot.lotKey, { lotId: lot.id, fromMs: -Infinity, toMs: Infinity })
      continue
    }

    let openedAt = -Infinity
    for (const relocation of relocations) {
      addTenure(relocation.fromFarmId, lot.lotKey, { lotId: lot.id, fromMs: openedAt, toMs: relocation.atMs })
      openedAt = relocation.atMs
    }
    const finalFarmId = relocations[relocations.length - 1].toFarmId
    addTenure(finalFarmId, lot.lotKey, { lotId: lot.id, fromMs: openedAt, toMs: Infinity })
    // The snapshot can disagree with the ledger's last destination (a farm
    // change written without a ledger entry). Index both so a row recorded
    // under the lot's current farm still resolves.
    if (finalFarmId !== lot.farmId) {
      addTenure(lot.farmId, lot.lotKey, { lotId: lot.id, fromMs: openedAt, toMs: Infinity })
    }
  }

  // Fase 3 (tier-a-merge-consumer-mapping.md §2.6): fold in ancestor members
  // from `LotLineageProjection` so a cross-family merge's absorbed lot still
  // resolves under its own historical `(farmId, lotKey)` — the survivor's
  // `lots` roster above only ever indexes the *current* lot, never lots a
  // merge folded into it. `self` rows are included too (harmless overlap
  // with the roster loop above when the member is still an active lot;
  // useful when it is not, e.g. an already-superseded intermediate lot).
  // `descendant` rows are excluded: a descendant's own history belongs to
  // its own identity going forward, not to this ancestor's projection.
  for (const row of lineageRows ?? []) {
    if (row.relation === 'descendant') continue
    const farmId = row.farmId.trim()
    const lotKey = row.memberLotKey.trim()
    const lotId = row.lotId.trim()
    if (!farmId || !lotKey || !lotId) continue
    addTenure(farmId, lotKey, {
      lotId,
      fromMs: projectionEpoch(row.effectiveFrom, -Infinity),
      toMs: projectionEpoch(row.effectiveTo, Infinity)
    })
  }

  return map
}

/**
 * v2.2 T3-B-06a: resolve a source event's lot to a canonical lotId, with
 * the same `(farmId, lotKey)` fallback that `resolveFeedLogLotId` does
 * on the auto-derivation side. The primary UI write path in
 * [src/screens/Feed.tsx](../screens/Feed.tsx) historically saved
 * FeedLogs with `lot=<lotKey>` but a null `lotId` column; those rows
 * would otherwise be silently skipped by the dataset because
 * `ensureAccumulator` requires a non-empty lotId. The fallback keeps
 * old rows visible in the report without any DB backfill.
 *
 * Returns the empty string when neither a direct `lotId` nor a
 * `(farmId, lotKey)` match resolves — caller must treat that as
 * "skip" (same rule as before this helper existed). B4: an ambiguous
 * `(farmId, lotKey)` that `recordedAt` cannot narrow to a single lot
 * also returns the empty string, matching how the mortality/weight
 * backfill script refuses to guess between colliding candidates.
 */
const resolveLotIdWithFallback = (
  candidateLotId: string | undefined,
  candidateLotKey: string | undefined,
  farmId: string | undefined,
  recordedAt: string | undefined,
  lotsByFarmAndKey: Map<string, LotFarmTenure[]>
): string => {
  const trimmedId = typeof candidateLotId === 'string' ? candidateLotId.trim() : ''
  if (trimmedId) return trimmedId
  const trimmedKey = typeof candidateLotKey === 'string' ? candidateLotKey.trim() : ''
  const trimmedFarm = typeof farmId === 'string' ? farmId.trim() : ''
  if (!trimmedKey || !trimmedFarm) return ''

  const tenures = lotsByFarmAndKey.get(`${trimmedFarm}::${trimmedKey}`)
  if (!tenures || tenures.length === 0) return ''

  const distinctLotIds = new Set(tenures.map((tenure) => tenure.lotId))
  if (distinctLotIds.size === 1) return tenures[0].lotId

  const atMs = parseInstantMs(recordedAt)
  if (!Number.isFinite(atMs)) return ''

  const matches = new Set(
    tenures.filter((tenure) => atMs >= tenure.fromMs && atMs < tenure.toMs).map((tenure) => tenure.lotId)
  )
  if (matches.size !== 1) return ''
  const [only] = matches
  return only
}

/**
 * Fase 3/7, consumer #13 (tier-a-merge-consumer-mapping.md §2.6 line 361):
 * `memberLotId → lotId` index built from `LotLineageProjection` rows, used to
 * redirect a resolved id onto the surviving lot of a merge, weighted by that
 * ancestor's fractional biomass contribution. This is the only path that
 * matters for `LotCostEvent`: that stream's `lotId` always resolves directly
 * (§1.B #13 — no `lot` label field, so `resolveLotIdWithFallback`'s
 * `(farmId, lotKey)` fallback never runs for it), so without this remap a
 * cost event still pointing at an absorbed lot id creates its own orphan row
 * instead of rolling into the survivor.
 *
 * Each `memberLotId` may resolve to more than one candidate survivor when the
 * lineage forked (e.g. split → merge → split): the same absorbed lot id can
 * legitimately belong to two different current lots' projections over two
 * disjoint time windows. `canonicalize` disambiguates by testing the source
 * event's own `recordedAt` against each candidate's `[effectiveFrom,
 * effectiveTo)` window — the same temporal-tenure pattern
 * `buildLotsByFarmAndKey`/`resolveLotIdWithFallback` already use for the
 * `(farmId, lotKey)` fallback above — instead of the pre-Fase-7 "most recent
 * `effectiveFrom` wins" tie-break, which picked a survivor without regard to
 * whether the event actually happened during that survivor's tenure.
 */
type CanonicalLotCandidate = { lotId: string; weight: number; fromMs: number; toMs: number }

const buildCanonicalLotIdByMemberId = (
  lineageRows?: readonly FallbackLineageRow[]
): Map<string, CanonicalLotCandidate[]> => {
  const map = new Map<string, CanonicalLotCandidate[]>()

  for (const row of lineageRows ?? []) {
    if (row.relation === 'descendant') continue
    const memberLotId = row.memberLotId.trim()
    const lotId = row.lotId.trim()
    if (!memberLotId || !lotId) continue

    const weight = typeof row.weight === 'number' && Number.isFinite(row.weight) ? row.weight : 1
    const bucket = map.get(memberLotId) ?? []
    bucket.push({
      lotId,
      weight,
      fromMs: projectionEpoch(row.effectiveFrom, -Infinity),
      toMs: projectionEpoch(row.effectiveTo, Infinity)
    })
    map.set(memberLotId, bucket)
  }

  return map
}

/**
 * Redirects a resolved lot id through the Fase 3/7 canonical remap, if
 * present, and returns the composed `weight` to apply to whatever magnitude
 * the caller is about to accumulate (`harvestKg`, `feedKg`, `amountCents`).
 * Empty input passes through unchanged with `weight: 1`. A single candidate
 * always wins outright (matches pre-Fase-7 behavior for the common case).
 * With two or more candidates, only the one whose `[effectiveFrom,
 * effectiveTo)` window contains `recordedAt` is used; an undecidable event
 * (missing/unparseable `recordedAt`, or a window match that isn't unique)
 * falls through to the un-redirected id at `weight: 1` rather than guessing —
 * mirrors the "genuinely undecidable rows are skipped" rule the B4 fix above
 * already established for the `(farmId, lotKey)` fallback.
 */
const canonicalize = (
  lotId: string,
  recordedAt: string | undefined,
  canonicalLotIdByMemberId: Map<string, CanonicalLotCandidate[]>
): { lotId: string; weight: number } => {
  if (!lotId) return { lotId, weight: 1 }

  const candidates = canonicalLotIdByMemberId.get(lotId)
  if (!candidates || candidates.length === 0) return { lotId, weight: 1 }
  if (candidates.length === 1) return { lotId: candidates[0].lotId, weight: candidates[0].weight }

  const atMs = parseInstantMs(recordedAt)
  if (!Number.isFinite(atMs)) return { lotId, weight: 1 }

  const matches = candidates.filter((candidate) => atMs >= candidate.fromMs && atMs < candidate.toMs)
  if (matches.length !== 1) return { lotId, weight: 1 }
  return { lotId: matches[0].lotId, weight: matches[0].weight }
}

/**
 * Fase 7 (DQ12, `phase7-dq-analysis.md` row #12): round-half-to-even applied
 * only at the final integer-cents output point, after every weighted
 * `amountCents × weight` contribution has already been summed as a float.
 * Never applied mid-accumulation.
 */
const bankersRoundCents = (value: number): number => {
  const floor = Math.floor(value)
  const diff = value - floor
  if (diff < 0.5) return floor
  if (diff > 0.5) return floor + 1
  return floor % 2 === 0 ? floor : floor + 1
}

const resolveCurrency = (currencies: Set<string>): string => {
  if (currencies.size === 0) return ''
  if (currencies.size > 1) return 'MIXED'
  const [only] = currencies
  return only
}

/**
 * v2.2 T3-B-05c: format the MIXED-currency triage string. Empty when the
 * lot has one or zero currencies (no triage needed); otherwise a stable,
 * comma-separated `CODE:count` list sorted by currency code ascending.
 * Sort ordering is deterministic across insertion order.
 */
const formatCurrencyBreakdown = (counts: Map<string, number>): string => {
  if (counts.size <= 1) return ''
  const sorted = Array.from(counts.entries()).sort((a, b) => a[0].localeCompare(b[0]))
  return sorted.map(([code, count]) => `${code}:${count}`).join(', ')
}

const computeCostPerKg = (
  totalCostsCents: number,
  totalHarvestKgNum: number,
  currencies: Set<string>
): string | null => {
  if (currencies.size !== 1) return null
  if (totalHarvestKgNum <= 0) return null
  const perKg = (totalCostsCents / 100) / totalHarvestKgNum
  if (!Number.isFinite(perKg)) return null
  return perKg.toFixed(4)
}

/**
 * v2.2 T3-B-05a: biological FCR = feed kg / harvest kg, formatted to 2
 * decimals. Returns `null` when either input is non-positive (cannot
 * divide by zero harvest; zero feed makes the ratio meaningless as a
 * husbandry KPI). Cost / currency are irrelevant here — this is a pure
 * mass ratio.
 */
const computeFcr = (totalFeedKgNum: number, totalHarvestKgNum: number): string | null => {
  if (totalFeedKgNum <= 0) return null
  if (totalHarvestKgNum <= 0) return null
  const ratio = totalFeedKgNum / totalHarvestKgNum
  if (!Number.isFinite(ratio)) return null
  return ratio.toFixed(2)
}

/**
 * v2.2 T3-B-06c: temporal-window filter for the three input event
 * streams. Callers (typically `reportDatasets.ts` `lotCostPerKg`
 * `fetchRows`) convert an operator-picked `ReportScopeV1` calendar-day
 * `fromDate`/`toDate` into inclusive UTC-instant bounds and pass them
 * here so the roll-up sees only events within the window.
 *
 * Semantics:
 * - Missing window OR both bounds missing → return the input untouched
 *   (lifetime; the pre-06c default).
 * - Only `fromRecordedAt` → events with `recordedAt >= fromRecordedAt`.
 * - Only `toRecordedAt` → events with `recordedAt <= toRecordedAt`.
 * - Both → events within the closed range `[fromRecordedAt, toRecordedAt]`.
 * - Degenerate range (from > to) → empty result.
 *
 * Events whose `recordedAt` cannot be parsed to a finite time are
 * dropped from the filtered output when a window is active. This matches
 * the shared `applyReportScope` behavior of dropping non-parseable dates
 * rather than passing them through.
 */
const applyRecordedAtWindow = <T extends { recordedAt?: string }>(
  events: readonly T[],
  window: { fromRecordedAt?: string; toRecordedAt?: string } | undefined
): readonly T[] => {
  if (!window) return events
  const fromMs = window.fromRecordedAt ? new Date(window.fromRecordedAt).getTime() : Number.NEGATIVE_INFINITY
  const toMs = window.toRecordedAt ? new Date(window.toRecordedAt).getTime() : Number.POSITIVE_INFINITY
  if (!Number.isFinite(fromMs) && Number.isNaN(fromMs)) return events
  if (!Number.isFinite(toMs) && Number.isNaN(toMs)) return events
  return events.filter((event) => {
    const t = typeof event.recordedAt === 'string' ? new Date(event.recordedAt).getTime() : NaN
    if (!Number.isFinite(t)) return false
    return t >= fromMs && t <= toMs
  })
}

/**
 * Build the cost-per-kg rows from the three input streams. See the file
 * docblock for row shape + semantics.
 */
export const buildLotCostPerKgRows = (args: {
  harvestLogs: readonly HarvestLog[]
  costEvents: readonly LotCostEvent[]
  lots: readonly Pick<Lot, 'id' | 'lotKey' | 'farmId' | 'productionUnitId' | 'species'>[]
  /** v2.2 T3-B-05a: FeedLog stream, rolled up per lot for the FCR column. Optional so callers that only care about cost/harvest keep working; missing / empty ⇒ every row emits `feedConversionRatio: null` and `totalFeedKg: '0.000'`. */
  feedLogs?: readonly FeedLog[]
  /** v2.2 T3-B-06c: optional inclusive-inclusive UTC-instant window applied to the three input event streams' `recordedAt` before roll-up. When omitted, all events roll up (lifetime; the pre-06c default). Callers derive the bounds from `ReportScopeV1.fromDate` / `toDate` (calendar-day → start-of-day and end-of-day UTC instants). */
  window?: { fromRecordedAt?: string; toRecordedAt?: string }
  /** v2.2 T3-B-06e: optional pre-computed `lotId → currentBiomassKg` map from `computeCurrentBiomassPerLot`. When provided, unharvested lots emit `feedConversionRatioInFlight` (feed to date ÷ current biomass). When omitted / empty, every row emits `feedConversionRatioInFlight: null` (matches pre-06e behavior). The map is passed in — not fetched — so `buildLotCostPerKgRows` stays pure and offline-safe. */
  currentBiomassByLotId?: ReadonlyMap<string, { biomassKg: number | null }>
  /** A1.1 fix (docs/architecture-concerns.md §A1.1, Hallazgo D4): optional LotEvent stream used to extend the `(farmId, lotKey)` fallback index with every historical farmId a lot has occupied (via `move`/`transfer`/`update` event metadata `fromFarmId`/`toFarmId`), so legacy rows recorded before a lot was transferred still resolve. Each farmId is indexed with the interval the lot occupied it, so a colliding `(farmId, lotKey)` pair is disambiguated by the row's own `recordedAt`. Omit to preserve prior current-farmId-only behavior. */
  lotEvents?: readonly LotEvent[]
  /**
   * Fase 3 (tier-a-merge-consumer-mapping.md §2.6, consumer #13): rows from
   * `LotLineageProjection` (`useLotLineageProjection` / `GET /lots/lineage`).
   * Feeds two things: (1) `buildLotsByFarmAndKey`'s `(farmId, lotKey)` index,
   * same as the other Capa E consumers; (2) a `memberLotId → lotId` remap
   * applied to every resolved id in this function, right before
   * `ensureAccumulator`, so a `LotCostEvent` still pinned to a lot a
   * cross-family merge absorbed rolls into the survivor instead of
   * synthesizing an orphan row. Only the remap is new for #13 — the
   * aggregation formulas, grouping, and output shape are unchanged. Omit to
   * preserve pre-Fase-3 behavior exactly.
   */
  lineageRows?: readonly FallbackLineageRow[]
  /** v2.2 findings.md #215: per lot id, stocked biomass and dead biomass (`buildLotFcrInputs`). Omit ⇒ eFCR/bFCR null. */
  fcrInputsByLotId?: ReadonlyMap<string, LotFcrInputs>
  /** v2.2 findings.md #215: per lot id, biomass when the window starts (only when the window has a start). A lot with no figure then falls back to its stocked biomass (it started inside the window). */
  startBiomassByLotId?: ReadonlyMap<string, { biomassKg: number | null }>
}): LotCostPerKgRow[] => {
  const harvestLogs = applyRecordedAtWindow(args.harvestLogs, args.window)
  const costEvents = applyRecordedAtWindow(args.costEvents, args.window)
  const feedLogs = applyRecordedAtWindow(args.feedLogs ?? [], args.window)

  const lotsById = new Map<string, Pick<Lot, 'id' | 'lotKey' | 'farmId' | 'productionUnitId' | 'species'>>()
  for (const lot of args.lots) {
    if (typeof lot.id === 'string' && lot.id) lotsById.set(lot.id, lot)
  }

  // v2.2 T3-B-06a: secondary lookup for source events whose `lotId`
  // column is blank but whose `(farmId, lot)` pair matches a known lot.
  const lotsByFarmAndKey = buildLotsByFarmAndKey(args.lots, args.lotEvents, args.lineageRows)

  // Fase 3, #13: `memberLotId → lotId` remap so an id that already resolved
  // (typically `LotCostEvent.lotId`, which has no label fallback to fall
  // back through) still lands on the surviving lot if a merge absorbed it.
  // `self` rows map a lot to itself (harmless identity entries); only
  // `ancestor` rows actually redirect anything.
  const canonicalLotIdByMemberId = buildCanonicalLotIdByMemberId(args.lineageRows)

  const acc = new Map<string, LotAccumulator>()

  for (const log of harvestLogs) {
    const target = canonicalize(
      resolveLotIdWithFallback(log.lotId, log.lot, log.farmId, log.recordedAt, lotsByFarmAndKey),
      log.recordedAt,
      canonicalLotIdByMemberId
    )
    const bucket = ensureAccumulator(acc, lotsById, target.lotId, {
      farmId: log.farmId,
      productionUnitId: log.productionUnitId
    })
    if (!bucket) continue
    // Fase 7 (§2.6 line 361): harvestKg is biomass-based, so it scales by the
    // ancestor's fractional weight. harvestCount is count-based (Fase 8,
    // fork pattern #9) and stays unweighted.
    bucket.totalHarvestKgNum += parseHarvestKg(log.harvestKg) * target.weight
    bucket.totalHarvestCount += parseHarvestCount(log.harvestCount)
    bucket.harvestEventCount += 1
  }

  // v2.2 T3-B-05a: parallel FeedLog roll-up. Mirror of the harvest loop
  // above. A lot with only feed events STILL emits a row (parallel to
  // today's harvest-only / cost-only emit rules).
  // v2.2 T3-B-06a: use the same `(farmId, lot)` fallback so historical
  // FeedLog rows saved without a `lotId` (per the pre-06a Feed.tsx
  // write path) still land against the correct lot without a DB backfill.
  for (const log of feedLogs) {
    const target = canonicalize(
      resolveLotIdWithFallback(log.lotId, log.lot, log.farmId, log.recordedAt, lotsByFarmAndKey),
      log.recordedAt,
      canonicalLotIdByMemberId
    )
    const bucket = ensureAccumulator(acc, lotsById, target.lotId, {
      farmId: log.farmId,
      productionUnitId: log.productionUnitId
    })
    if (!bucket) continue
    bucket.totalFeedKgNum += parseFeedKg(log.feedKg) * target.weight
    bucket.feedEventCount += 1
  }

  for (const event of costEvents) {
    // LotCostEvent has no `lot` (lotKey) field — the schema requires
    // `lotId` — so the fallback is a no-op here but we route through
    // the same helper for symmetry with the harvest / feed loops.
    const target = canonicalize(
      resolveLotIdWithFallback(
        event.lotId,
        undefined,
        event.farmId,
        event.recordedAt,
        lotsByFarmAndKey
      ),
      event.recordedAt,
      canonicalLotIdByMemberId
    )
    const bucket = ensureAccumulator(acc, lotsById, target.lotId, {
      farmId: event.farmId,
      productionUnitId: event.productionUnitId
    })
    if (!bucket) continue
    const cents = typeof event.amountCents === 'number' && Number.isFinite(event.amountCents)
      ? event.amountCents
      : 0
    // Fase 7 (DQ12): accumulate the weighted contribution as a float; the
    // integer-cents rounding happens once, at row-emit time, via
    // `bankersRoundCents` — never mid-accumulation.
    const weightedCents = cents * target.weight
    bucket.totalCostsCents += weightedCents
    if (typeof event.currency === 'string' && event.currency.trim().length > 0) {
      const code = event.currency.trim()
      bucket.currencies.add(code)
      bucket.currencyEventCounts.set(code, (bucket.currencyEventCounts.get(code) ?? 0) + 1)
    }
    bucket.costEventCount += 1
    const categoryKey = bucketFor(event.categoryPrefix)
    bucket.categoryCents[categoryKey] += weightedCents
  }

  const rows: LotCostPerKgRow[] = []
  for (const bucket of acc.values()) {
    // Fase 7 (DQ12): the only point where accumulated (possibly fractional,
    // weighted) cents are rounded to an integer, via banker's rounding.
    const totalCostsCents = bankersRoundCents(bucket.totalCostsCents)
    const totalCostsMajor = (totalCostsCents / 100).toFixed(2)
    const expectedFcrRange = resolveExpectedFcrRange(bucket.speciesKey)
    const expectedFcrMin = expectedFcrRange?.min ?? null
    const expectedFcrMax = expectedFcrRange?.max ?? null
    const expectedFcrRangeLabel = expectedFcrRange
      ? `${expectedFcrRange.min.toFixed(2)}-${expectedFcrRange.max.toFixed(2)}`
      : null
    const feedConversionRatioInFlight = computeFcrInFlight(
      bucket.totalFeedKgNum,
      args.currentBiomassByLotId?.get(bucket.lotId)?.biomassKg ?? null,
      bucket.totalHarvestKgNum > 0
    )
    // v2.2 findings.md #215: eFCR / bFCR. A harvested lot with no biomass
    // figure left counts as 0 kg at the end.
    const fcrInputs = args.fcrInputsByLotId?.get(bucket.lotId)
    const startBiomassKg = args.startBiomassByLotId?.get(bucket.lotId)?.biomassKg ?? fcrInputs?.stockedBiomassKg ?? null
    const currentBiomassKg = args.currentBiomassByLotId?.get(bucket.lotId)?.biomassKg ?? null
    const endBiomassKg = currentBiomassKg ?? (bucket.totalHarvestKgNum > 0 ? 0 : null)
    const netProductionKg = computeNetProductionKg({
      harvestKg: bucket.totalHarvestKgNum,
      endBiomassKg,
      startBiomassKg
    })
    const deadBiomassKg = fcrInputs?.deadBiomassKg ?? null
    const economicFcr = computeEconomicFcr(bucket.totalFeedKgNum, netProductionKg)
    const biologicalFcr = computeBiologicalFcr(bucket.totalFeedKgNum, netProductionKg, deadBiomassKg)

    rows.push({
      lotId: bucket.lotId,
      lotKey: bucket.lotKey,
      farmId: bucket.farmId,
      productionUnitId: bucket.productionUnitId,
      speciesKey: bucket.speciesKey,
      expectedFcrMin,
      expectedFcrMax,
      expectedFcrRangeLabel,
      totalHarvestKg: bucket.totalHarvestKgNum.toFixed(3),
      totalHarvestCount: String(bucket.totalHarvestCount),
      harvestEventCount: bucket.harvestEventCount,
      totalFeedKg: bucket.totalFeedKgNum.toFixed(3),
      feedEventCount: bucket.feedEventCount,
      feedConversionRatio: computeFcr(bucket.totalFeedKgNum, bucket.totalHarvestKgNum),
      feedConversionRatioInFlight,
      feedConversionRatioInFlightBand: computeFcrInFlightBand({
        feedConversionRatioInFlight,
        expectedFcrMin,
        expectedFcrMax
      }),
      startBiomassKg: startBiomassKg === null ? null : startBiomassKg.toFixed(3),
      netProductionKg: netProductionKg === null ? null : netProductionKg.toFixed(3),
      deadBiomassKg: deadBiomassKg === null ? null : deadBiomassKg.toFixed(3),
      economicFcr: economicFcr === null ? null : economicFcr.toFixed(2),
      biologicalFcr: biologicalFcr === null ? null : biologicalFcr.toFixed(2),
      totalCostsCents,
      totalCostsMajor,
      currency: resolveCurrency(bucket.currencies),
      costEventCount: bucket.costEventCount,
      costPerKgMajor: computeCostPerKg(totalCostsCents, bucket.totalHarvestKgNum, bucket.currencies),
      currencyBreakdown: formatCurrencyBreakdown(bucket.currencyEventCounts),
      costByCategoryMajor: {
        G: (bucket.categoryCents.G / 100).toFixed(2),
        N: (bucket.categoryCents.N / 100).toFixed(2),
        H: (bucket.categoryCents.H / 100).toFixed(2),
        L: (bucket.categoryCents.L / 100).toFixed(2),
        other: (bucket.categoryCents.other / 100).toFixed(2)
      },
      categorySharePct: {
        G: formatPercent(bucket.categoryCents.G, totalCostsCents),
        N: formatPercent(bucket.categoryCents.N, totalCostsCents),
        H: formatPercent(bucket.categoryCents.H, totalCostsCents),
        L: formatPercent(bucket.categoryCents.L, totalCostsCents),
        other: formatPercent(bucket.categoryCents.other, totalCostsCents)
      }
    })
  }

  // Deterministic ordering: by lotKey (natural string sort) then lotId as tie-breaker.
  rows.sort((a, b) => {
    const byKey = a.lotKey.localeCompare(b.lotKey)
    if (byKey !== 0) return byKey
    return a.lotId.localeCompare(b.lotId)
  })

  return rows
}
